/**
 * What a flat actually costs: the cash to sign, the monthly burden, and the
 * total handed over by the end of the loan.
 *
 * Pure functions over plain numbers, like `scenario.ts` — no React, no
 * fetching. Every rate arrives as an argument so the caller decides what is a
 * default and what the user has overridden.
 */

import {
  CSI_RATE,
  DISBURSEMENTS,
  DMTO_ANCIEN,
  DMTO_ANCIEN_FIRST_TIME,
  DMTO_NEUF,
  EMOLUMENT_TIERS,
  VAT,
} from "./financeDefaults";

export type PropertyKind = "ancien" | "neuf";
/**
 * Bank group contracts charge a flat premium on the capital originally
 * borrowed; delegated insurers usually charge on what is still owed, so the
 * premium falls every year. Over 20 years the difference is thousands.
 */
export type InsuranceBasis = "initial" | "remaining";

// ---------------------------------------------------------------- notary ---

export interface NotaryFees {
  /** Transfer duties — by far the largest part on an existing home. */
  duties: number;
  /** The notary's own regulated fee, VAT included. */
  emoluments: number;
  /** Land-registry security contribution. */
  csi: number;
  /** Searches and filings the notary advances on your behalf. */
  disbursements: number;
  total: number;
  /** Total as a share of the price — the "7 to 8%" people quote. */
  rate: number;
}

/** The regulated sliding scale, applied tranche by tranche. */
export function emoluments(price: number): number {
  let remaining = price;
  let floor = 0;
  let total = 0;
  for (const tier of EMOLUMENT_TIERS) {
    if (remaining <= 0) break;
    const width = tier.upTo - floor;
    const slice = Math.min(remaining, width);
    total += slice * tier.rate;
    remaining -= slice;
    floor = tier.upTo;
  }
  return total * (1 + VAT);
}

export function dutyRate(kind: PropertyKind, firstTimeBuyer: boolean): number {
  if (kind === "neuf") return DMTO_NEUF;
  return firstTimeBuyer ? DMTO_ANCIEN_FIRST_TIME : DMTO_ANCIEN;
}

/**
 * "Frais de notaire" on the price alone.
 *
 * Agency fees are deliberately not in the base: when the buyer pays them and
 * the deed says so, they sit outside the duty calculation. Renovation budget
 * is not in it either — you are not buying the works.
 */
export function notaryFees(
  price: number,
  kind: PropertyKind,
  firstTimeBuyer: boolean,
): NotaryFees {
  const duties = price * dutyRate(kind, firstTimeBuyer);
  const emo = emoluments(price);
  const csi = price * CSI_RATE;
  const total = duties + emo + csi + DISBURSEMENTS;
  return {
    duties,
    emoluments: emo,
    csi,
    disbursements: DISBURSEMENTS,
    total,
    rate: price > 0 ? total / price : 0,
  };
}

// ------------------------------------------------------------------ loan ---

export interface LoanTerms {
  principal: number;
  /** Nominal annual rate, as a fraction. */
  rate: number;
  years: number;
  /** Annual insurance rate on the covered capital. */
  insuranceRate: number;
  insuranceBasis: InsuranceBasis;
  /** Share of the capital covered: 1 for one borrower, 2 for a couple at 100%. */
  quotity: number;
}

export interface AmortRow {
  month: number;
  year: number;
  interest: number;
  principal: number;
  insurance: number;
  /** Capital still owed after this payment. */
  balance: number;
}

export interface LoanResult {
  /** Capital and interest only — what the bank calls the mensualité. */
  payment: number;
  /** First month's insurance premium; constant unless the basis is remaining. */
  firstInsurance: number;
  totalInterest: number;
  totalInsurance: number;
  months: number;
  rows: AmortRow[];
}

/** Standard fixed-rate annuity. */
export function monthlyPayment(
  principal: number,
  annualRate: number,
  years: number,
): number {
  const n = Math.round(years * 12);
  if (n <= 0 || principal <= 0) return 0;
  const i = annualRate / 12;
  if (i === 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
}

export function amortise(terms: LoanTerms): LoanResult {
  const { principal, rate, years, insuranceRate, insuranceBasis, quotity } = terms;
  const n = Math.round(years * 12);
  const payment = monthlyPayment(principal, rate, years);
  const coveredRate = insuranceRate * quotity;
  const flatInsurance = (principal * coveredRate) / 12;

  const rows: AmortRow[] = [];
  let balance = principal;
  let totalInterest = 0;
  let totalInsurance = 0;

  for (let m = 1; m <= n && principal > 0; m++) {
    const interest = (balance * rate) / 12;
    // The final instalment absorbs the rounding drift rather than leaving a
    // few cents outstanding for ever.
    const capital = Math.min(payment - interest, balance);
    const insurance =
      insuranceBasis === "initial" ? flatInsurance : (balance * coveredRate) / 12;
    balance = Math.max(0, balance - capital);
    totalInterest += interest;
    totalInsurance += insurance;
    rows.push({
      month: m,
      year: Math.ceil(m / 12),
      interest,
      principal: capital,
      insurance,
      balance,
    });
  }

  return {
    payment,
    firstInsurance: rows[0]?.insurance ?? 0,
    totalInterest,
    totalInsurance,
    months: n,
    rows,
  };
}

/**
 * The all-in annual rate: the one figure that makes two offers comparable,
 * because it folds in the fees and the insurance a headline rate hides.
 *
 * Solved by bisection on the monthly rate that discounts every payment back to
 * the cash actually placed at your disposal. Newton would converge faster, but
 * this cannot diverge, and it runs once per keystroke on 300 rows.
 */
export function taeg(
  loan: LoanResult,
  principal: number,
  upfrontFees: number,
): number | null {
  const net = principal - upfrontFees;
  if (net <= 0 || loan.rows.length === 0) return null;

  const present = (monthly: number) =>
    loan.rows.reduce(
      (sum, r) => sum + (r.principal + r.interest + r.insurance) / Math.pow(1 + monthly, r.month),
      0,
    );

  let lo = 0;
  let hi = 0.05; // 60%+ a year — far above any legal offer
  if (present(hi) > net) return null;
  for (let k = 0; k < 200; k++) {
    const mid = (lo + hi) / 2;
    if (present(mid) > net) lo = mid;
    else hi = mid;
  }
  return Math.pow(1 + (lo + hi) / 2, 12) - 1;
}

// ------------------------------------------------------------- the whole ---

export interface PurchaseInput {
  price: number;
  /** Buyer-paid agency commission, outside the duty base. */
  agencyFees: number;
  works: number;
  kind: PropertyKind;
  firstTimeBuyer: boolean;

  deposit: number;
  years: number;
  rate: number;
  insuranceRate: number;
  insuranceBasis: InsuranceBasis;
  quotity: number;
  arrangementFee: number;
  /** Loan guarantee (caution or hypothèque), as a share of the borrowed sum. */
  guaranteeRate: number;
  brokerFee: number;

  /** Annual running costs, in euros. */
  propertyTax: number;
  serviceCharges: number;
  homeInsurance: number;

  /** Net monthly household income; 0 when not given. */
  income: number;
}

export interface Purchase {
  notary: NotaryFees;
  /** Everything the purchase costs before any borrowing. */
  acquisition: number;
  /** Bank charges, which scale with the borrowed sum. */
  arrangementFee: number;
  guaranteeFee: number;
  brokerFee: number;
  /** What has to be borrowed once the deposit is applied. */
  loanAmount: number;
  /** Cash you must actually have on the day. */
  cashNeeded: number;
  loan: LoanResult;
  taeg: number | null;

  monthly: {
    payment: number;
    insurance: number;
    propertyTax: number;
    serviceCharges: number;
    homeInsurance: number;
    total: number;
  };

  /** Loan payment plus insurance over income — the ratio a bank tests. */
  debtRatio: number | null;
  /** Everything monthly over income — the ratio your bank account tests. */
  burdenRatio: number | null;

  /** Price + fees + interest + insurance, over the life of the loan. */
  totalPaid: number;
}

export function computePurchase(input: PurchaseInput): Purchase {
  const notary = notaryFees(input.price, input.kind, input.firstTimeBuyer);
  const acquisition = input.price + notary.total + input.agencyFees + input.works;

  // Bank charges scale with the loan, and the loan depends on them. One pass
  // over the pre-fee figure is enough: the second-order term is a few euros.
  const bare = Math.max(0, acquisition - input.deposit);
  const guaranteeFee = bare * input.guaranteeRate;
  const bankFees = input.arrangementFee + guaranteeFee + input.brokerFee;
  const loanAmount = Math.max(0, acquisition + bankFees - input.deposit);

  const loan = amortise({
    principal: loanAmount,
    rate: input.rate,
    years: input.years,
    insuranceRate: input.insuranceRate,
    insuranceBasis: input.insuranceBasis,
    quotity: input.quotity,
  });

  const monthlyInsurance =
    input.insuranceBasis === "initial"
      ? loan.firstInsurance
      : loan.months > 0
        ? loan.totalInsurance / loan.months
        : 0;

  const monthly = {
    payment: loan.payment,
    insurance: monthlyInsurance,
    propertyTax: input.propertyTax / 12,
    serviceCharges: input.serviceCharges / 12,
    homeInsurance: input.homeInsurance / 12,
    total: 0,
  };
  monthly.total =
    monthly.payment +
    monthly.insurance +
    monthly.propertyTax +
    monthly.serviceCharges +
    monthly.homeInsurance;

  const bankTested = loan.payment + loan.firstInsurance;

  return {
    notary,
    acquisition,
    arrangementFee: input.arrangementFee,
    guaranteeFee,
    brokerFee: input.brokerFee,
    loanAmount,
    cashNeeded: Math.min(input.deposit, acquisition + bankFees),
    loan,
    taeg: taeg(loan, loanAmount, bankFees),
    monthly,
    debtRatio: input.income > 0 ? bankTested / input.income : null,
    burdenRatio: input.income > 0 ? monthly.total / input.income : null,
    totalPaid:
      acquisition + bankFees + loan.totalInterest + loan.totalInsurance,
  };
}
